import { Bell, ChevronRight, ClipboardList, Download, FileText, Satellite } from 'lucide-react';

export function ProfileSection({
	name,
	installed,
	isIOS,
	canInstall,
	installing,
	onInstall,
	notificationPermission,
	pushSubscribed,
	enablingNotifications,
	notificationMessage,
	onEnableNotifications,
	gpsEnabled,
	onGpsInfo,
	onPayslips,
	onBadge,
	build,
}: {
	name: string | null;
	installed: boolean;
	isIOS: boolean;
	canInstall: boolean;
	installing: boolean;
	onInstall: () => void;
	notificationPermission: NotificationPermission | 'unsupported';
	pushSubscribed: boolean;
	enablingNotifications: boolean;
	notificationMessage: string | null;
	onEnableNotifications: () => void;
	gpsEnabled: boolean;
	onGpsInfo: () => void;
	onPayslips: () => void;
	onBadge: () => void;
	build: string;
}) {
	return (
		<section aria-labelledby="profile-title" className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
			<h2 id="profile-title" className="text-xl font-bold">
				Il tuo profilo
			</h2>
			{name && <p className="mt-1 break-words text-base text-muted-foreground">{name}</p>}

			<div className="mt-5 divide-y divide-border overflow-hidden rounded-2xl border border-border">
				{[
					{
						label: 'Buste paga',
						detail: 'Consulta i documenti mensili',
						Icon: FileText,
						action: onPayslips,
					},
					{
						label: 'Tesserino',
						detail: 'Identificazione e documenti personali',
						Icon: ClipboardList,
						action: onBadge,
					},
				].map(({ label, detail, Icon, action }) => (
					<button
						key={label}
						type="button"
						onClick={action}
						className="flex w-full items-center gap-3 px-4 py-4 text-left active:bg-muted"
					>
						<Icon className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
						<span className="min-w-0 flex-1">
							<span className="block font-semibold">{label}</span>
							<span className="block text-sm text-muted-foreground">{detail}</span>
						</span>
						<ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
					</button>
				))}
			</div>

			<h3 className="mb-3 mt-6 text-base font-semibold">Sul tuo telefono</h3>
			<div className="space-y-3">
				<details className="rounded-2xl border border-border p-4">
					<summary className="cursor-pointer font-semibold">
						<Download className="mr-2 inline h-5 w-5 text-primary" aria-hidden="true" />
						{installed ? 'App installata' : 'Installa l’app'}
					</summary>
					<p className="mt-3 text-sm leading-relaxed text-muted-foreground">
						{installed
							? 'Puoi aprire Ore in Cantiere direttamente dalla schermata Home.'
							: 'Aggiungi Ore in Cantiere alla schermata Home per aprirla più rapidamente. Puoi timbrare anche da questo browser.'}
					</p>
					{!installed &&
						(canInstall ? (
							<button
								type="button"
								disabled={installing}
								onClick={onInstall}
								className="mt-3 min-h-11 rounded-xl bg-primary px-4 py-2 font-semibold text-white disabled:opacity-50"
							>
								{installing ? 'Apertura…' : 'Installa sul telefono'}
							</button>
						) : (
							<p className="mt-3 text-sm leading-relaxed">
								{isIOS
									? 'In Safari, apri Condividi e scegli “Aggiungi alla schermata Home”.'
									: 'Apri il menu del browser e cerca “Installa app” o “Aggiungi alla schermata Home”. Se non trovi la voce, puoi continuare qui.'}
							</p>
						))}
				</details>

				<details className="rounded-2xl border border-border p-4">
					<summary className="cursor-pointer font-semibold">
						<Bell className="mr-2 inline h-5 w-5 text-primary" aria-hidden="true" />
						Promemoria di timbratura
						<span className="ml-2 text-sm font-normal text-muted-foreground">
							{pushSubscribed && notificationPermission === 'granted' ? 'Attivi' : 'Non attivi'}
						</span>
					</summary>
					<p className="mt-3 text-sm leading-relaxed text-muted-foreground">
						{notificationPermission === 'denied'
							? 'Le notifiche sono disattivate nelle impostazioni del browser. Puoi riattivarle da lì quando vuoi; puoi continuare a timbrare.'
							: notificationPermission === 'unsupported'
								? 'Questo browser non rende disponibili le notifiche. Puoi continuare a timbrare.'
								: pushSubscribed
									? 'Riceverai i promemoria disponibili per la tua azienda.'
									: 'Attiva le notifiche per ricevere i promemoria disponibili per la tua azienda.'}
					</p>
					{!pushSubscribed &&
						notificationPermission !== 'denied' &&
						notificationPermission !== 'unsupported' && (
							<button
								type="button"
								disabled={enablingNotifications}
								onClick={onEnableNotifications}
								className="mt-3 min-h-11 rounded-xl border border-primary px-4 py-2 font-semibold text-primary disabled:opacity-50"
							>
								{enablingNotifications ? 'Attivazione…' : 'Attiva promemoria'}
							</button>
						)}
					{notificationMessage && (
						<p role="status" className="mt-3 text-sm">
							{notificationMessage}
						</p>
					)}
				</details>

				{gpsEnabled && (
					<button
						type="button"
						id="profile-gps-info"
						onClick={onGpsInfo}
						className="flex w-full items-center gap-3 rounded-2xl border border-border p-4 text-left active:bg-muted"
					>
						<Satellite className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
						<span className="flex-1">
							<span className="block font-semibold">Posizione alla timbratura</span>
							<span className="block text-sm text-muted-foreground">Come viene usato il GPS</span>
						</span>
						<ChevronRight className="h-5 w-5 shrink-0" aria-hidden="true" />
					</button>
				)}
			</div>

			<details className="mb-2 mt-6 rounded-xl bg-muted p-4 text-sm">
				<summary className="cursor-pointer font-semibold">Informazioni per l’assistenza</summary>
				<p className="mt-3">
					Per correggere una timbratura o recuperare l’accesso, contatta il tuo responsabile.
				</p>
				<p className="mt-2 break-all text-muted-foreground">Versione app: {build}</p>
			</details>
		</section>
	);
}
